import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { generateToken, TOKEN_HEADER } from '../utils/tokenUtils';
import { API_ENDPOINTS } from '../config/constants';
import '../styles/events.css';

const STATS = [
  { n: '500+', l: 'engineers in the community' },
  { n: '5★', l: 'average event rating' },
  { n: 'Free', l: 'intros every month' },
  { n: '100%', l: 'live, hands-on sessions' },
];

const getFormatDisplay = (event) => {
  if (event.location_type === 'virtual') return '💻 Online';
  if (event.location_type === 'physical') return `📍 ${event.location_name || 'In-person'}`;
  return '📍 In-person';
};

const getPreferredPrice = (event) => {
  if (!Array.isArray(event.prices) || event.prices.length === 0) {
    if (event.price === undefined || event.price === null || event.price === '') {
      return null;
    }

    const parsedLegacyPrice = Number(event.price);
    if (!Number.isFinite(parsedLegacyPrice)) {
      return null;
    }

    return { currency: event.currency || 'INR', price: parsedLegacyPrice, isenabled: true };
  }

  const enabledPrices = event.prices.filter((item) => item.isenabled !== false);
  return enabledPrices.find((item) => item.currency === 'INR') || enabledPrices[0] || event.prices[0] || null;
};

const isFreeEvent = (event) =>
  event.is_free === true || (() => {
    const preferred = getPreferredPrice(event);
    return !preferred || Number(preferred.price) === 0;
  })();

const formatPriceLabel = (event) => {
  const preferred = getPreferredPrice(event);
  if (!preferred) {
    return 'RSVP';
  }

  if (Number(preferred.price) === 0) {
    return 'Free';
  }

  return `${preferred.currency} ${Number(preferred.price).toFixed(2)}`;
};

const isWorkshopEvent = (event) =>
  (event.category_name || '').toLowerCase().includes('workshop') ||
  (event.title || '').toLowerCase().includes('workshop');

const parseEventUtcDate = (dateValue) => {
  if (!dateValue) return new Date(NaN);
  if (dateValue instanceof Date) return dateValue;
  if (typeof dateValue === 'number') return new Date(dateValue);
  if (typeof dateValue !== 'string') return new Date(dateValue);

  const trimmed = dateValue.trim();
  const hasTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);
  const isoLike = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const normalized = hasTimezone ? isoLike : `${isoLike}Z`;

  return new Date(normalized);
};

const formatDateParts = (dateString) => {
  const date = parseEventUtcDate(dateString);
  return {
    day: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
    d: date.getDate(),
    mo: date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
    time: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
  };
};

const formatCountdown = (targetDate) => {
  const diff = parseEventUtcDate(targetDate) - new Date();
  if (diff <= 0) return null;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const secs = Math.floor((diff % (1000 * 60)) / 1000);
  return { days, hours, mins, secs };
};

const Events = () => {
  const navigate = useNavigate();
  const isAttendeeLoggedIn = Boolean(localStorage.getItem('attendeeToken'));
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showRSVPModal, setShowRSVPModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [rsvpForm, setRSVPForm] = useState({
    name: '', email: '', mobile: '', status: 'going', guests_count: 0,
  });
  const [rsvpLoading, setRSVPLoading] = useState(false);
  const [rsvpSuccess, setRSVPSuccess] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyDone, setNotifyDone] = useState(false);
  const countdownRef = useRef(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    filterEvents();
  }, [events, activeTab, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  // Countdown timer for next upcoming event
  const nextEvent = events
    .filter(e => parseEventUtcDate(e.start_datetime) > new Date())
    .sort((a, b) => parseEventUtcDate(a.start_datetime) - parseEventUtcDate(b.start_datetime))[0];

  useEffect(() => {
    if (!nextEvent) return;
    const tick = () => setCountdown(formatCountdown(nextEvent.start_datetime));
    tick();
    countdownRef.current = setInterval(tick, 1000);
    return () => clearInterval(countdownRef.current);
  }, [nextEvent?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events?status=published&limit=100`, {
        headers: { [TOKEN_HEADER]: generateToken() },
      });
      const data = await response.json();
      if (data.success) setEvents(data.data || []);
    } catch (error) {
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterEvents = () => {
    const now = new Date();
    const startOfWeek = new Date(now); startOfWeek.setHours(0, 0, 0, 0); startOfWeek.setDate(now.getDate() - now.getDay());
    const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 7);
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

    let filtered = [...events];
    if (activeTab === 'free') filtered = filtered.filter(isFreeEvent);
    else if (activeTab === 'workshops') filtered = filtered.filter(isWorkshopEvent);
    else if (activeTab === 'past') filtered = filtered.filter(e => parseEventUtcDate(e.start_datetime) <= now);
    else if (activeTab === 'this-week') filtered = filtered.filter(e => { const d = parseEventUtcDate(e.start_datetime); return d >= startOfWeek && d < endOfWeek; });
    else if (activeTab === 'this-month') filtered = filtered.filter(e => { const d = parseEventUtcDate(e.start_datetime); return d >= startOfMonth && d <= endOfMonth; });
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(e =>
        e.title.toLowerCase().includes(q) ||
        (e.description && e.description.toLowerCase().includes(q)) ||
        (e.location_name && e.location_name.toLowerCase().includes(q))
      );
    }
    filtered.sort((a, b) => parseEventUtcDate(a.start_datetime) - parseEventUtcDate(b.start_datetime));
    setFilteredEvents(filtered);
  };

  const handleRSVPClick = (event, e) => {
    if (e) e.stopPropagation();
    setSelectedEvent(event);
    setShowRSVPModal(true);
    setRSVPSuccess(false);
  };

  const handleRSVPSubmit = async (e) => {
    e.preventDefault();
    setRSVPLoading(true);
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/events/${selectedEvent.id}/rsvp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          [TOKEN_HEADER]: generateToken(),
        },
        body: JSON.stringify(rsvpForm),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setRSVPSuccess(true);
        setRSVPForm({ name: '', email: '', mobile: '', status: 'going', guests_count: 0 });
        setTimeout(() => { fetchEvents(); setShowRSVPModal(false); }, 2500);
      } else {
        alert(data.error || 'Failed to submit RSVP. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting RSVP:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setRSVPLoading(false);
    }
  };

  const closeModal = () => {
    setShowRSVPModal(false);
    setSelectedEvent(null);
    setRSVPSuccess(false);
  };

  const handleEventClick = (event) => {
    sessionStorage.setItem('selectedEvent', JSON.stringify(event));
    const titleSlug = event.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const typeSlug = (event.location_type || 'event').toLowerCase();
    const locationSlug = event.location_name
      ? event.location_name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      : 'tba';
    const slug = event.slug || `${titleSlug}-${typeSlug}-${locationSlug}`;
    navigate(`/events/details?id=${event.id}&slug=${slug}`);
  };

  const upcomingEvents = filteredEvents.filter(e => parseEventUtcDate(e.start_datetime) > new Date());
  const pastEvents = filteredEvents.filter(e => parseEventUtcDate(e.start_datetime) <= new Date());

  const now = new Date();
  const startOfWeek = new Date(now); startOfWeek.setHours(0,0,0,0); startOfWeek.setDate(now.getDate() - now.getDay());
  const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 7);
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

  const tabs = [
    { key: 'all', label: `All (${events.length})` },
    { key: 'free', label: 'Free' },
    { key: 'workshops', label: 'Workshops' },
    { key: 'this-week', label: `This Week (${events.filter(e => { const d = parseEventUtcDate(e.start_datetime); return d >= startOfWeek && d < endOfWeek; }).length})` },
    { key: 'this-month', label: `This Month (${events.filter(e => { const d = parseEventUtcDate(e.start_datetime); return d >= startOfMonth && d <= endOfMonth; }).length})` },
    { key: 'past', label: `Past (${events.filter(e => parseEventUtcDate(e.start_datetime) <= now).length})` },
  ];

  const featuredEvent = nextEvent;

  return (
    <div className="ep-page">

      {/* ── HERO ── */}
      <header className="ep-hero">
        <div className="ep-hero-glow" aria-hidden="true" />
        <div className="ep-hero-inner">
          <div className="ep-hero-left">
            <span className="ep-live-badge">
              <span className="ep-pulse-dot" />
              Live events · {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <h1 className="ep-hero-title">
              Learn by building it —{' '}
              <span className="ep-red">live, in the room.</span>
            </h1>
            <p className="ep-hero-sub">
              Hands-on workshops and free intro sessions. Start free, go deep when you're ready. No recordings — just you, a small cohort, and real code that ships.
            </p>
            <div className="ep-hero-actions">
              {featuredEvent && (
                <button className="ep-btn-primary" onClick={() => handleRSVPClick(featuredEvent)}>
                  Register for the next event →
                </button>
              )}
              <a href="#ep-events" className="ep-btn-ghost">Browse all events</a>
              {isAttendeeLoggedIn ? (
                <button className="ep-btn-ghost" onClick={() => navigate('/workshop')}>
                  📚 My Workshops
                </button>
              ) : (
                <button className="ep-btn-ghost" onClick={() => navigate('/events/login?returnTo=/workshop')}>
                  🔑 Attendee Login
                </button>
              )}
              <button className="ep-btn-ghost" onClick={() => navigate('/events/feedback')}>
                Share Feedback
              </button>
            </div>
            <div className="ep-hero-stats">
              {STATS.map((s, i) => (
                <React.Fragment key={s.n}>
                  {i > 0 && <span className="ep-stat-divider" />}
                  <span><b>{s.n}</b> {s.l}</span>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Featured event card */}
          {featuredEvent ? (
            <div className="ep-featured-card">
              <div className="ep-featured-glow" aria-hidden="true" />
              <div className="ep-featured-inner">
                <div className="ep-featured-top">
                  <span className="ep-featured-label">Next live event</span>
                  <span className="ep-featured-date-label">
                    {(() => {
                      const p = formatDateParts(featuredEvent.start_datetime);
                      return `${p.day} · ${p.mo} ${p.d}`;
                    })()}
                  </span>
                </div>
                <h2 className="ep-featured-title">{featuredEvent.title}</h2>
                {featuredEvent.description && (
                  <p className="ep-featured-desc">
                    {featuredEvent.description.substring(0, 100)}
                    {featuredEvent.description.length > 100 ? '...' : ''}
                  </p>
                )}
                {countdown && (
                  <div className="ep-countdown">
                    {[
                      { v: countdown.days, l: 'Days' },
                      { v: countdown.hours, l: 'Hrs' },
                      { v: countdown.mins, l: 'Min' },
                      { v: countdown.secs, l: 'Sec', red: true },
                    ].map(({ v, l, red }) => (
                      <div key={l} className="ep-cd-box">
                        <div className={`ep-cd-num${red ? ' ep-red' : ''}`}>{String(v).padStart(2, '0')}</div>
                        <div className="ep-cd-label">{l}</div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="ep-featured-footer">
                  <div>
                    <span className="ep-featured-price">
                      {formatPriceLabel(featuredEvent)}
                    </span>
                    {!isFreeEvent(featuredEvent) && (
                      <span className="ep-per-seat"> / seat</span>
                    )}
                  </div>
                  <button
                    className="ep-btn-primary ep-btn-sm"
                    onClick={() => handleRSVPClick(featuredEvent)}
                  >
                    Register →
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="ep-featured-card ep-featured-empty">
              <div className="ep-featured-inner">
                <p className="ep-muted">No upcoming events scheduled yet.</p>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* ── EVENTS LIST ── */}
      <section id="ep-events" className="ep-events-section">
        <div className="ep-events-inner">
          <div className="ep-events-header">
            <div>
              <h2 className="ep-section-title">
                {activeTab === 'past' ? 'Past Events'
                  : activeTab === 'this-week' ? 'This Week\'s Events'
                  : activeTab === 'this-month' ? 'This Month\'s Events'
                  : activeTab === 'free' ? 'Free Events'
                  : activeTab === 'workshops' ? 'Workshops'
                  : 'All Upcoming Events'}
              </h2>
              <p className="ep-muted ep-count-label">
                {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}{activeTab !== 'past' ? ' · register in two clicks' : ''}
              </p>
            </div>
            <div className="ep-tabs">
              {tabs.map(t => (
                <button
                  key={t.key}
                  className={`ep-tab${activeTab === t.key ? ' ep-tab-active' : ''}`}
                  onClick={() => setActiveTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="ep-search-wrap">
            <span className="ep-search-icon">🔍</span>
            <input
              type="text"
              className="ep-search"
              placeholder="Search events..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          {loading ? (
            <div className="ep-loading">
              <div className="ep-spinner" />
              <p>Loading events...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="ep-empty">
              <div className="ep-empty-icon">📅</div>
              <h3>No events found</h3>
              <p className="ep-muted">Try adjusting your filters or search query</p>
            </div>
          ) : (
            <>
              {/* Upcoming events (hidden when "Past" tab is active) */}
              {activeTab !== 'past' && upcomingEvents.length > 0 && (
                <div className="ep-event-list">
                  {upcomingEvents.map(event => {
                    const dp = formatDateParts(event.start_datetime);
                    const free = isFreeEvent(event);
                    return (
                      <div
                        key={event.id}
                        className="ep-event-row"
                        onClick={() => handleEventClick(event)}
                      >
                        <div className="ep-row-date">
                          <div className="ep-row-day">{dp.day}</div>
                          <div className="ep-row-d">{dp.d}</div>
                          <div className="ep-row-mo">{dp.mo}</div>
                        </div>
                        <div className="ep-row-details">
                          <div className="ep-row-title-wrap">
                            <h3 className="ep-row-title">{event.title}</h3>
                            {event.is_featured && (
                              <span className="ep-hot-badge">⭐ Featured</span>
                            )}
                          </div>
                          {event.description && (
                            <p className="ep-row-desc">
                              {event.description.substring(0, 120)}
                              {event.description.length > 120 ? '...' : ''}
                            </p>
                          )}
                          <div className="ep-row-chips">
                            {event.category_name && (
                              <span className={`ep-chip ${free ? 'ep-chip-free' : 'ep-chip-paid'}`}>
                                {event.category_name}
                              </span>
                            )}
                            <span className="ep-chip ep-chip-muted">{getFormatDisplay(event)}</span>
                            {dp.time && (
                              <span className="ep-chip ep-chip-muted">🕐 {dp.time}</span>
                            )}
                          </div>
                        </div>
                        <div className="ep-row-action" onClick={e => e.stopPropagation()}>
                          <div className="ep-row-price">
                            {free
                              ? <span className="ep-price-free">Free</span>
                              : <span className="ep-price-paid">{formatPriceLabel(event)}</span>
                            }
                          </div>
                          {event.going_count > 0 && (
                            <div className="ep-row-attendees">👥 {event.going_count} attending</div>
                          )}
                          <button
                            className={`ep-btn-primary ep-btn-sm${free ? ' ep-btn-free' : ''}`}
                            onClick={() => handleRSVPClick(event)}
                          >
                            {free ? 'Join Free →' : 'Register →'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Past events — shown inline for "past" tab, or as a secondary section for other tabs */}
              {pastEvents.length > 0 && (
                <>
                  {activeTab !== 'past' && upcomingEvents.length > 0 && (
                    <h3 className="ep-past-title">Past Events</h3>
                  )}
                  <div className={`ep-event-list${activeTab !== 'past' ? ' ep-past-list' : ''}`}>
                    {pastEvents.map(event => {
                      const dp = formatDateParts(event.start_datetime);
                      const isPastTab = activeTab === 'past';
                      const free = isFreeEvent(event);
                      return (
                        <div
                          key={event.id}
                          className={`ep-event-row${isPastTab ? '' : ' ep-event-row-past'}`}
                          onClick={() => handleEventClick(event)}
                        >
                          <div className="ep-row-date">
                            <div className="ep-row-day">{dp.day}</div>
                            <div className="ep-row-d">{dp.d}</div>
                            <div className="ep-row-mo">{dp.mo}</div>
                          </div>
                          <div className="ep-row-details">
                            <div className="ep-row-title-wrap">
                              <h3 className="ep-row-title">{event.title}</h3>
                              <span className="ep-past-badge">Past</span>
                            </div>
                            {event.description && (
                              <p className="ep-row-desc">
                                {event.description.substring(0, 120)}
                                {event.description.length > 120 ? '...' : ''}
                              </p>
                            )}
                            <div className="ep-row-chips">
                              {event.category_name && (
                                <span className="ep-chip ep-chip-muted">{event.category_name}</span>
                              )}
                              <span className="ep-chip ep-chip-muted">{getFormatDisplay(event)}</span>
                            </div>
                          </div>
                          <div className="ep-row-action" onClick={e => e.stopPropagation()}>
                            <div className="ep-row-attendees">👥 {event.going_count || 0} attended</div>
                            <button
                              className="ep-btn-ghost ep-btn-sm"
                              onClick={() => handleEventClick(event)}
                            >
                              View Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* No results within a time-range tab */}
              {(activeTab === 'this-week' || activeTab === 'this-month') && filteredEvents.length === 0 && (
                <div className="ep-empty">
                  <div className="ep-empty-icon">📅</div>
                  <h3>No events {activeTab === 'this-week' ? 'this week' : 'this month'}</h3>
                  <p className="ep-muted">Check back soon or browse all events</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── STATS & ABOUT ── */}
      <section className="ep-stats-section">
        <div className="ep-stats-inner">
          <div className="ep-stats-grid">
            {STATS.map(s => (
              <div key={s.n} className="ep-stat-card">
                <div className="ep-stat-n">{s.n}</div>
                <div className="ep-stat-l">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="ep-founder-card">
            <div className="ep-founder-avatar">CA</div>
            <div className="ep-founder-quote">
              <p>"I show up to every session personally. No pre-recorded content — just the frameworks, the feedback and the mentorship I wish I'd had earlier. We cap workshops small so you actually get attention."</p>
              <div className="ep-founder-name">Chaitanya Allidona · Founder &amp; AI Productivity Trainer, Lead With Tech</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── NOTIFY ── */}
      <section className="ep-notify-section">
        <div className="ep-notify-inner">
          <div className="ep-notify-text">
            <h3>Not ready to register yet?</h3>
            <p className="ep-muted">Drop your email and we'll tell you when the next free intro opens up. No spam.</p>
          </div>
          {notifyDone ? (
            <div className="ep-notify-done">✓ You're on the list — see you at the next intro.</div>
          ) : (
            <div className="ep-notify-form">
              <input
                type="email"
                className="ep-notify-input"
                placeholder="you@email.com"
                value={notifyEmail}
                onChange={e => setNotifyEmail(e.target.value)}
              />
              <button
                className="ep-btn-primary"
                onClick={() => { if (notifyEmail.trim()) setNotifyDone(true); }}
              >
                Notify me →
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="ep-cta-section">
        <div className="ep-cta-inner">
          <span className="ep-cta-eyebrow">The next cohort starts soon</span>
          <h2 className="ep-cta-title">Stop watching. Start building.</h2>
          <p className="ep-muted ep-cta-sub">
            Seats are limited and the next workshop is filling fast.
          </p>
          {featuredEvent && (
            <button className="ep-btn-primary ep-btn-lg" onClick={() => handleRSVPClick(featuredEvent)}>
              Register for the next event →
            </button>
          )}
        </div>
      </section>

      {/* ── RSVP MODAL ── */}
      {showRSVPModal && selectedEvent && (
        <div className="ep-modal-overlay" onClick={closeModal}>
          <div className="ep-modal" onClick={e => e.stopPropagation()}>
            <button className="ep-modal-close" onClick={closeModal}>✕</button>

            {rsvpSuccess ? (
              <div className="ep-rsvp-success">
                <div className="ep-success-icon">✅</div>
                <h2>RSVP Confirmed!</h2>
                <p>Thank you for registering for <strong>{selectedEvent.title}</strong></p>
                <p className="ep-muted">We've sent a confirmation email to {rsvpForm.email}</p>
              </div>
            ) : (
              <>
                <h2 className="ep-modal-title">RSVP for {selectedEvent.title}</h2>
                <p className="ep-modal-sub">
                  {(() => {
                    const p = formatDateParts(selectedEvent.start_datetime);
                    return `${p.day}, ${p.mo} ${p.d} · ${p.time}`;
                  })()}
                </p>

                <form onSubmit={handleRSVPSubmit} className="ep-rsvp-form">
                  <div className="ep-form-group">
                    <label htmlFor="ep-name">Full Name *</label>
                    <input
                      type="text"
                      id="ep-name"
                      value={rsvpForm.name}
                      onChange={e => setRSVPForm({ ...rsvpForm, name: e.target.value })}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label htmlFor="ep-email">Email Address *</label>
                    <input
                      type="email"
                      id="ep-email"
                      value={rsvpForm.email}
                      onChange={e => setRSVPForm({ ...rsvpForm, email: e.target.value })}
                      required
                      placeholder="your.email@example.com"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label htmlFor="ep-mobile">Mobile Number (Optional)</label>
                    <input
                      type="tel"
                      id="ep-mobile"
                      value={rsvpForm.mobile}
                      onChange={e => setRSVPForm({ ...rsvpForm, mobile: e.target.value })}
                      placeholder="+91 98765 43210"
                    />
                  </div>
                  <div className="ep-form-group">
                    <label>RSVP Status *</label>
                    <div className="ep-status-options">
                      {[
                        { val: 'going', icon: '✓', label: 'Going' },
                        { val: 'interested', icon: '⭐', label: 'Interested' },
                      ].map(({ val, icon, label }) => (
                        <label key={val} className="ep-status-option">
                          <input
                            type="radio"
                            name="ep-status"
                            value={val}
                            checked={rsvpForm.status === val}
                            onChange={e => setRSVPForm({ ...rsvpForm, status: e.target.value })}
                          />
                          <span className="ep-status-label">
                            <span>{icon}</span> {label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="ep-form-group">
                    <label htmlFor="ep-guests">Number of Guests</label>
                    <select
                      id="ep-guests"
                      value={rsvpForm.guests_count}
                      onChange={e => setRSVPForm({ ...rsvpForm, guests_count: parseInt(e.target.value) })}
                    >
                      <option value="0">Just me</option>
                      <option value="1">+1 guest</option>
                      <option value="2">+2 guests</option>
                      <option value="3">+3 guests</option>
                      <option value="4">+4 guests</option>
                      <option value="5">+5 guests</option>
                    </select>
                  </div>
                  <button type="submit" className="ep-submit-btn" disabled={rsvpLoading}>
                    {rsvpLoading ? 'Submitting...' : 'Confirm RSVP'}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Events;
